import React, { useState, useEffect } from "react";
import { Col, Row, Container, Card, Spinner, Alert, Button } from "react-bootstrap";
import './AppDownloadAnalytics.css';
import { API_URL } from "./config";

const AppDownloadAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    summary: {},
    analytics: [],
    topCountries: [],
    topCities: [],
    topSuburbs: [],
    osDistribution: []
  });

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/app-downloads/analytics`);
      const result = await response.json();
      
      if (result.success) {
        setAnalytics({
          summary: result.data.summary || {},
          analytics: result.data.analytics || [],
          topCountries: result.data.top_countries || [],
          topCities: result.data.top_cities || [],
          topSuburbs: result.data.top_suburbs || [],
          osDistribution: result.data.os_distribution || []
        });
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading App Download Analytics...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger" className="m-3">
          <Alert.Heading>Error Loading Analytics</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={handleRefresh}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">App Download Analytics</h2>
            <Button 
              variant="outline-primary" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body className="text-center">
              <h3 className="text-success">{analytics.summary?.total_downloads || 0}</h3>
              <p className="mb-0 text-muted">Total Downloads</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body className="text-center">
              <h3 className="text-info">{analytics.summary?.unique_visitors || 0}</h3>
              <p className="mb-0 text-muted">Unique Visitors</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body className="text-center">
              <h3 className="text-success">{analytics.summary?.successful_downloads || 0}</h3>
              <p className="mb-0 text-muted">Successful</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} className="mb-3">
          <Card className="summary-card">
            <Card.Body className="text-center">
              <h3 className="text-warning">{analytics.summary?.failed_downloads || 0}</h3>
              <p className="mb-0 text-muted">Failed</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Analytics Grid */}
      <Row>
        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Top 5 Countries</h5>
            </Card.Header>
            <Card.Body>
              {analytics.topCountries && analytics.topCountries.length > 0 ? (
                <div className="ranking-list">
                  {analytics.topCountries.slice(0, 5).map((item, index) => (
                    <div key={index} className="ranking-item">
                      <span className="rank-badge country-rank">{index + 1}</span>
                      <span className="item-name">{item.country_name || 'Unknown'}</span>
                      <span className="item-count">{item.download_count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Top 5 Cities</h5>
            </Card.Header>
            <Card.Body>
              {analytics.topCities && analytics.topCities.length > 0 ? (
                <div className="ranking-list">
                  {analytics.topCities.slice(0, 5).map((item, index) => (
                    <div key={index} className="ranking-item">
                      <span className="rank-badge city-rank">{index + 1}</span>
                      <span className="item-name">{item.city_name || 'Unknown'}</span>
                      <span className="item-count">{item.download_count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Top 5 Suburbs</h5>
            </Card.Header>
            <Card.Body>
              {analytics.topSuburbs && analytics.topSuburbs.length > 0 ? (
                <div className="ranking-list">
                  {analytics.topSuburbs.slice(0, 5).map((item, index) => (
                    <div key={index} className="ranking-item">
                      <span className="rank-badge suburb-rank">{index + 1}</span>
                      <span className="item-name">{item.suburb_name || 'Unknown'}</span>
                      <span className="item-count">{item.download_count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* OS Distribution */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Operating System Distribution</h5>
            </Card.Header>
            <Card.Body>
              {analytics.osDistribution && analytics.osDistribution.length > 0 ? (
                <div className="os-distribution">
                  {analytics.osDistribution.map((item, index) => (
                    <div key={index} className="os-item">
                      <div className="os-info">
                        <span className="os-name">{item.operating_system}</span>
                        <span className="os-percentage">{item.percentage}%</span>
                      </div>
                      <div className="progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center">No OS data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Detailed Statistics</h5>
            </Card.Header>
            <Card.Body>
              <div className="details-list">
                <div className="detail-item">
                  <span className="detail-label">Android Downloads:</span>
                  <span className="detail-value">{analytics.summary?.android_downloads || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">iOS Downloads:</span>
                  <span className="detail-value">{analytics.summary?.ios_downloads || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Top Country:</span>
                  <span className="detail-value">{analytics.summary?.top_country || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Download:</span>
                  <span className="detail-value">
                    {analytics.summary?.last_download_date 
                      ? new Date(analytics.summary.last_download_date).toLocaleDateString()
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AppDownloadAnalytics;
